/**
 * University Browse Page - Modern Layout
 * Browse universities with modern filters and card design
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, Search, SlidersHorizontal } from 'lucide-react';
import { UniversityFilters } from '../components/UniversityFilters';
import { UniversityList } from '../components/UniversityList';
import { universityAPI } from '../api/university-api';
import { SEO, Header } from '../../../shared/components';
import type { University, FilterOptions, BrowseFilters } from '../types';

const UniversityBrowsePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [universities, setUniversities] = useState<University[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  // Parse filters from URL
  const getFiltersFromURL = (): BrowseFilters => {
    return {
      field: searchParams.get('field') || undefined,
      country: searchParams.get('country') || undefined,
      budgetMin: searchParams.get('budget_min') ? parseInt(searchParams.get('budget_min')!) : undefined,
      budgetMax: searchParams.get('budget_max') ? parseInt(searchParams.get('budget_max')!) : undefined,
      rankingMax: searchParams.get('ranking_max') ? parseInt(searchParams.get('ranking_max')!) : undefined,
      page: searchParams.get('page') ? parseInt(searchParams.get('page')!) : 1,
      sort: searchParams.get('sort') || 'ranking'
    };
  };

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const options = await universityAPI.getFilterOptions();
        setFilterOptions(options);
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    };
    loadFilterOptions();
  }, []);

  // Load universities when filters change
  useEffect(() => {
    const loadUniversities = async () => {
      setIsLoading(true);
      try {
        const filters = getFiltersFromURL();
        const result = await universityAPI.browseUniversities(filters);
        setUniversities(result.universities);
        setPagination(result.pagination);
      } catch (err) {
        console.error('Failed to load universities:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadUniversities();
  }, [searchParams]);

  // Update URL when filters change
  const handleFilterChange = (filters: BrowseFilters) => {
    const params: any = {};

    if (filters.field) params.field = filters.field;
    if (filters.country) params.country = filters.country;
    if (filters.budgetMin) params.budget_min = filters.budgetMin.toString();
    if (filters.budgetMax) params.budget_max = filters.budgetMax.toString();
    if (filters.rankingMax) params.ranking_max = filters.rankingMax.toString();
    if (filters.sort && filters.sort !== 'ranking') params.sort = filters.sort;
    if (filters.page && filters.page !== 1) params.page = filters.page.toString();

    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const filters = getFiltersFromURL();
    handleFilterChange({ ...filters, page });
  };

  const handleResetFilters = () => {
    setSearchParams({});
  };

  const handleUniversityClick = (slug: string) => {
    // Navigate to university detail page, preserving current filters in state
    navigate(`/university/${slug}`, {
      state: { from: `/university/browse?${searchParams.toString()}` }
    });
  };

  if (!filterOptions) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center gap-3">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
          <span className="text-foreground font-medium">Loading universities...</span>
        </div>
      </div>
    );
  }

  // Get active filter count
  const activeFilters = getFiltersFromURL();
  const activeFilterCount = [
    activeFilters.field,
    activeFilters.country,
    activeFilters.budgetMin,
    activeFilters.rankingMax
  ].filter(Boolean).length;

  return (
    <>
      <SEO
        title="Browse Universities"
        description="Explore top universities worldwide with smart filters - find your perfect match"
      />

      <Header />

      <div className="min-h-screen bg-background">
        {/* Modern Hero Header */}
        <div className="bg-gradient-to-br from-primary via-primary to-emerald-700 border-b border-white/10">
          <div className="container mx-auto px-6 py-12">
            <div className="max-w-3xl">
              <h1 className="text-5xl font-extrabold text-white mb-3 leading-tight tracking-tight">
                Discover Your Perfect University
              </h1>
              <p className="text-lg text-white/90 mb-6 leading-relaxed">
                Explore {pagination.total.toLocaleString()} world-class universities. Filter by location, budget, ranking, and field of study.
              </p>

              {/* Search Stats */}
              <div className="flex flex-wrap items-center gap-4">
                <div className="bg-white/15 backdrop-blur-md px-5 py-3 rounded-xl border border-white/20 flex items-center gap-2">
                  <Search className="h-5 w-5 text-white" />
                  <span className="text-white font-semibold">
                    {pagination.total} Universities
                  </span>
                </div>

                {activeFilterCount > 0 && (
                  <div className="bg-white/15 backdrop-blur-md px-5 py-3 rounded-xl border border-white/20 flex items-center gap-2">
                    <SlidersHorizontal className="h-5 w-5 text-white" />
                    <span className="text-white font-semibold">
                      {activeFilterCount} Active Filter{activeFilterCount > 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content with Modern Layout */}
        <div className="container mx-auto px-6 py-10">
          <div className="grid grid-cols-12 gap-8">
            {/* Modern Sticky Sidebar Filters */}
            <div className="col-span-12 lg:col-span-3">
              <div className="sticky top-24">
                <UniversityFilters
                  filters={getFiltersFromURL()}
                  filterOptions={filterOptions}
                  onFilterChange={handleFilterChange}
                  onReset={handleResetFilters}
                />
              </div>
            </div>

            {/* University Results */}
            <div className="col-span-12 lg:col-span-9">
              <UniversityList
                universities={universities}
                pagination={pagination}
                isLoading={isLoading}
                onPageChange={handlePageChange}
                onUniversityClick={handleUniversityClick}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UniversityBrowsePage;
